import { JOURNEYS, NAME_MAX, formatCedis, shiftNet } from '@accra/shared';
import { TRAFFIC_KINDS } from '../../content/traffic';
import type { RunState } from '../../game/RunState';
import { activeRoute, legAt } from '../../world/route';
import { el } from '../components/el';
import { Screen } from '../components/screen';
import { OverExtras } from './overExtras';

function row(label: string, value: string, tone = ''): [HTMLElement, HTMLElement] {
  return [el('dt', '', label), el('dd', tone, value)];
}

/** End-of-shift summary: what you earned, what you paid, and what's in (or owed from) the wallet. */
export class GameOver extends Screen {
  private readonly title = el('h2', 'crash-title');
  private readonly body = el('div', 'over-body');
  private walletCell: HTMLElement | null = null;
  private readonly synced = el('p', 'synced');
  private readonly nameForm = el('form', 'name-form');
  private readonly nameInput = el('input', 'name-input');
  private readonly nameMessage = el('small', 'name-message');
  readonly missions = el('div', 'over-missions');
  readonly extras: OverExtras;
  private readonly again: HTMLButtonElement;

  constructor(
    parent: HTMLElement, onAgain: () => void, onMenu: () => void, private readonly onName: (name: string) => Promise<string | null>,
    onShare: ConstructorParameters<typeof OverExtras>[0],
    onCard: ConstructorParameters<typeof OverExtras>[1],
  ) {
    super(parent, 'over');
    this.extras = new OverExtras(onShare, onCard);
    this.nameInput.maxLength = NAME_MAX;
    this.nameInput.placeholder = 'e.g. Kofi from Madina';
    const post = el('button', 'btn small', 'Post score');
    this.nameForm.append(el('label', '', 'Put your name on the leaderboard'), this.nameInput, post, this.nameMessage);
    this.nameForm.addEventListener('submit', (e) => {
      e.preventDefault();
      void this.submitName();
    });
    this.panel.append(this.title, this.body, this.nameForm, this.missions);
    const actions = el('div', 'actions');
    this.again = this.button('New shift', onAgain, 'primary');
    actions.append(this.again, this.button('Menu', onMenu));
    this.panel.append(actions);
  }

  /** `retry` = this was a challenge, so the main button plays it again. */
  showWith(state: RunState, best: number, walletPesewas: number, retry = false): void {
    const { stats } = state;
    this.again.textContent = retry ? 'Try again' : 'New shift';
    this.extras.reset();
    const brokeDown = state.endReason === 'breakdown';
    this.title.textContent = state.arrived ? 'Ayekoo! Yɛadu!' : brokeDown ? 'EI CHALE! Wu car nu asɛi!' : 'Shift done!';
    const last = TRAFFIC_KINDS.find((k) => k.id === state.crashCause)?.label;
    const journey = JOURNEYS[activeRoute().id];
    const cause =
      state.arrived && journey
        ? `You made it to ${activeRoute().to}! Journey bonus +${formatCedis(journey.bonusPesewas)} and +${journey.bonusScore.toLocaleString()} points.`
        : state.crashCause === 'fuel'
        ? `Out of fuel on ${legAt(state.bus.s).name}. Fill up at a petrol station next time.`
        : brokeDown
          ? `Too many knocks. The last one was ${last ?? 'traffic'} on ${legAt(state.bus.s).name}.`
          : `You parked up on ${legAt(state.bus.s).name}.`;
    const score = Math.floor(stats.score);
    const net = shiftNet(stats.faresPesewas, stats.finesPesewas, stats.repairsPesewas, stats.fuelPesewas);
    const offences = [
      stats.redLightsRun ? `${stats.redLightsRun} red light${stats.redLightsRun > 1 ? 's' : ''}` : '',
      stats.pedestrianOffences ? `${stats.pedestrianOffences} zebra` : '',
      stats.rearEnds ? `${stats.rearEnds} rear-end` : '',
      stats.checkpointsRun ? `${stats.checkpointsRun} checkpoint${stats.checkpointsRun > 1 ? 's' : ''} run` : '',
      stats.papersPaid ? `${stats.papersPaid} papers` : '',
      stats.dashTen + stats.dashTwenty ? `${stats.dashTen + stats.dashTwenty} dash to police` : '',
    ].filter(Boolean);
    const grid = el('dl', 'stats');
    const walletRow = row('Wallet', formatCedis(walletPesewas), walletPesewas < 0 ? 'bad' : 'good');
    this.walletCell = walletRow[1];
    grid.append(
      ...row('Distance', `${Math.floor(stats.metres)} m`),
      ...row('Passengers', `${stats.passengersDropped} dropped / ${stats.passengersCarried} carried`),
      ...row('Fares', formatCedis(stats.faresPesewas), 'good'),
      ...row('Police fines', stats.finesPesewas ? `−${formatCedis(stats.finesPesewas)} (${offences.join(', ')})` : 'None', stats.finesPesewas ? 'bad' : ''),
      ...row('Fuel', `${stats.fuelPesewas ? `−${formatCedis(stats.fuelPesewas)} (${stats.fuelUnits}% bought) · ` : ''}${Math.round(state.fuel.units)}% left${retry ? '' : ', carries over'}`, stats.fuelPesewas ? 'bad' : ''),
      ...row('Repairs', stats.repairsPesewas ? `−${formatCedis(stats.repairsPesewas)} (${stats.crashes} crash, ${stats.scrapes} scrape)` : 'None', stats.repairsPesewas ? 'bad' : ''),
      ...row('This shift', formatCedis(net), net < 0 ? 'bad' : 'good'),
      ...walletRow,
    );
    const debt = walletPesewas < 0 ? el('p', 'debt-note', `You owe ${formatCedis(-walletPesewas)}. Your next fares go to paying it off.`) : el('span');
    this.body.replaceChildren(
      el('p', 'cause', cause),
      el('div', 'final-score', score.toLocaleString()),
      el('p', 'best', score >= best ? 'NEW BEST!' : `Best ${best.toLocaleString()}`),
      this.extras.root,
      grid,
      debt,
      this.synced,
    );
    this.synced.textContent = 'Saving to your account…';
    this.show();
  }

  /** Shows the name box only for players who haven't picked a name yet. */
  askName(hasName: boolean): void {
    this.nameForm.style.display = hasName ? 'none' : '';
    this.nameMessage.textContent = '';
  }

  private async submitName(): Promise<void> {
    const name = this.nameInput.value.trim();
    if (name.length < 2) {
      this.nameMessage.textContent = 'At least 2 letters.';
      return;
    }
    const error = await this.onName(name);
    this.nameMessage.textContent = error ?? `You're on the board as ${name}!`;
    if (!error) this.nameInput.disabled = true;
  }

  /** Called when the server confirms the shift: its wallet is the source of truth. */
  confirm(walletPesewas: number, rankToday: number | null, accepted: boolean): void {
    if (this.walletCell) {
      this.walletCell.textContent = formatCedis(walletPesewas);
      this.walletCell.className = walletPesewas < 0 ? 'bad' : 'good';
    }
    this.synced.textContent = !accepted ? 'The server could not accept this shift.' : rankToday ? `Saved · #${rankToday} today` : 'Saved';
  }

  offline(): void {
    this.synced.textContent = 'Offline: this shift will be sent next time you play.';
    this.extras.offline();
  }
}
